import { useState } from 'react'

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { Area } from './Area'
import { ChipComponent as Chip } from './chip'
import { Currency } from './Currency'
import { Floor } from './Floor'
import { FloorPlanButton } from './FloorPlanButton'
import { Rooms } from './Rooms'
import { getComparator } from './utils'

const PropertyTableCell = ({
  property,
  field,
  onClick,
  children,
  ...props
}) => {
  const handleClick = () => {
    // Don't handle clicks for floor plan URLs or upcoming properties
    if (
      field === 'FLOOR_PLAN_URL' ||
      !onClick ||
      property.status === 'UPCOMING'
    ) {
      return
    }
    onClick(property)
  }

  const shouldShowPointer =
    onClick && field !== 'FLOOR_PLAN_URL' && property.status !== 'UPCOMING'

  return (
    <TableCell
      {...props}
      sx={{
        ...props.sx,
        cursor: shouldShowPointer ? 'pointer' : 'default',
      }}
      onClick={handleClick}
    >
      {children}
    </TableCell>
  )
}

export const TableComponent = ({ fields, onClick, properties }) => {
  const { t } = useTranslation()
  const [order, setOrder] = useState('asc')
  const [orderBy, setOrderBy] = useState('number')

  const handleClickSort = (property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const sorted = properties?.toSorted?.(getComparator(order, orderBy)) ?? []

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {fields?.includes('NUMBER') && (
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'number'}
                  direction={orderBy === 'number' ? order : 'asc'}
                  onClick={() => handleClickSort('number')}
                >
                  {t('Number')}
                </TableSortLabel>
              </TableCell>
            )}
            {fields?.includes('BUILDING') && (
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'building'}
                  direction={orderBy === 'building' ? order : 'asc'}
                  onClick={() => handleClickSort('building')}
                >
                  {t('Building')}
                </TableSortLabel>
              </TableCell>
            )}
            {fields?.includes('ROOMS') && (
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'rooms'}
                  direction={orderBy === 'rooms' ? order : 'asc'}
                  onClick={() => handleClickSort('rooms')}
                >
                  {t('Rooms')}
                </TableSortLabel>
              </TableCell>
            )}
            {fields?.includes('AREA') && (
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'area'}
                  direction={orderBy === 'area' ? order : 'asc'}
                  onClick={() => handleClickSort('area')}
                >
                  {t('Area')}
                </TableSortLabel>
              </TableCell>
            )}
            {fields?.includes('FLOOR') && (
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'floor'}
                  direction={orderBy === 'floor' ? order : 'asc'}
                  onClick={() => handleClickSort('floor')}
                >
                  {t('Floor')}
                </TableSortLabel>
              </TableCell>
            )}
            {fields?.includes('PRICE') && (
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'price'}
                  direction={orderBy === 'price' ? order : 'asc'}
                  onClick={() => handleClickSort('price')}
                >
                  {t('Price')}
                </TableSortLabel>
              </TableCell>
            )}
            {fields?.includes('MONTHLY_FEE') && (
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'monthlyFee'}
                  direction={orderBy === 'monthlyFee' ? order : 'asc'}
                  onClick={() => handleClickSort('monthlyFee')}
                >
                  {t('Monthly fee')}
                </TableSortLabel>
              </TableCell>
            )}
            {fields?.includes('FLOOR_PLAN_URL') && (
              <TableCell>{t('Floor plan')}</TableCell>
            )}
            {fields?.includes('STATUS') && (
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleClickSort('status')}
                >
                  {t('Status')}
                </TableSortLabel>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted?.map((p) => (
            <TableRow key={p.id}>
              {fields?.includes('NUMBER') && (
                <PropertyTableCell
                  property={p}
                  field="NUMBER"
                  onClick={onClick}
                >
                  {p.number}
                </PropertyTableCell>
              )}
              {fields?.includes('BUILDING') && (
                <PropertyTableCell
                  property={p}
                  field="BUILDING"
                  onClick={onClick}
                >
                  {p.building}
                </PropertyTableCell>
              )}
              {fields?.includes('ROOMS') && (
                <PropertyTableCell property={p} field="ROOMS" onClick={onClick}>
                  <Rooms value={p.rooms} />
                </PropertyTableCell>
              )}
              {fields?.includes('AREA') && (
                <PropertyTableCell property={p} field="AREA" onClick={onClick}>
                  <Area value={p.area} />
                </PropertyTableCell>
              )}
              {fields?.includes('FLOOR') && (
                <PropertyTableCell property={p} field="FLOOR" onClick={onClick}>
                  <Floor value={p.floor} />
                </PropertyTableCell>
              )}
              {fields?.includes('PRICE') && (
                <PropertyTableCell property={p} field="PRICE" onClick={onClick}>
                  <Currency value={p.price} />
                </PropertyTableCell>
              )}
              {fields?.includes('MONTHLY_FEE') && (
                <PropertyTableCell
                  property={p}
                  field="MONTHLY_FEE"
                  onClick={onClick}
                >
                  <Currency value={p.monthlyFee} />
                </PropertyTableCell>
              )}
              {fields?.includes('FLOOR_PLAN_URL') && (
                <PropertyTableCell
                  property={p}
                  field="FLOOR_PLAN_URL"
                  onClick={onClick}
                >
                  <FloorPlanButton url={p.floorPlanUrl} />
                </PropertyTableCell>
              )}
              {fields?.includes('STATUS') && (
                <PropertyTableCell
                  property={p}
                  field="STATUS"
                  onClick={onClick}
                >
                  <Chip status={p.status} />
                </PropertyTableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
